/**
 * Keycloak Configuration and Instance
 * Keycloak-ის კონფიგურაცია და ინსტანცია
 *
 * Session persistence: tokens are stored in localStorage so that
 * a page reload restores the authenticated session without redirecting
 * to Keycloak (which returns 400 on prompt=none).
 */

import Keycloak from 'keycloak-js';
import type { KeycloakConfig } from '../types';

let keycloakInstance: Keycloak | null = null;
let initPromise: Promise<boolean> | null = null;

const TOKEN_STORAGE_KEY = 'kc_token';
const REFRESH_TOKEN_STORAGE_KEY = 'kc_refreshToken';
const ID_TOKEN_STORAGE_KEY = 'kc_idToken';

/**
 * Save Keycloak tokens to localStorage
 */
export function saveTokens(keycloak: Keycloak): void {
  if (keycloak.token) localStorage.setItem(TOKEN_STORAGE_KEY, keycloak.token);
  if (keycloak.refreshToken) localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, keycloak.refreshToken);
  if (keycloak.idToken) localStorage.setItem(ID_TOKEN_STORAGE_KEY, keycloak.idToken);
}

/**
 * Clear stored tokens
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
}

/**
 * Get stored tokens for init()
 */
export function getStoredTokens(): { token?: string; refreshToken?: string; idToken?: string } {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY) || undefined;
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || undefined;
  const idToken = localStorage.getItem(ID_TOKEN_STORAGE_KEY) || undefined;
  return { token, refreshToken, idToken };
}

/**
 * Initialize Keycloak instance
 */
export function initKeycloak(config: KeycloakConfig): Keycloak {
  if (keycloakInstance) {
    return keycloakInstance;
  }

  keycloakInstance = new Keycloak({
    url: config.url,
    realm: config.realm,
    clientId: config.clientId,
  });

  return keycloakInstance;
}

/**
 * Initialize Keycloak with singleton pattern for init()
 * Prevents "can only be initialized once" error in React StrictMode
 */
export async function initKeycloakOnce(
  keycloak: Keycloak,
  options: Keycloak.KeycloakInitOptions
): Promise<boolean> {
  if (initPromise) {
    return initPromise;
  }

  initPromise = keycloak.init(options);
  return initPromise;
}

/**
 * Get existing Keycloak instance
 */
export function getKeycloak(): Keycloak | null {
  return keycloakInstance;
}

/**
 * Clear Keycloak instance (for testing)
 */
export function clearKeycloak(): void {
  keycloakInstance = null;
}

/**
 * Default Keycloak init options
 * No onLoad — we restore tokens from localStorage instead of redirecting to Keycloak.
 */
export const defaultInitOptions: Keycloak.KeycloakInitOptions = {
  pkceMethod: 'S256',
  checkLoginIframe: false,
  responseMode: 'query',
};

/**
 * Extract user from Keycloak token
 */
export function extractUserFromToken(keycloak: Keycloak) {
  const tokenParsed = keycloak.tokenParsed;

  if (!tokenParsed) {
    return null;
  }

  return {
    id: tokenParsed.sub || '',
    email: tokenParsed.email || '',
    firstName: tokenParsed.given_name,
    lastName: tokenParsed.family_name,
    username: tokenParsed.preferred_username,
    emailVerified: tokenParsed.email_verified,
    tenantId: tokenParsed.tenant_id,
    roles: [
      ...(tokenParsed.realm_access?.roles || []),
      ...(Object.values(tokenParsed.resource_access || {}).flatMap(
        (resource: any) => resource.roles || []
      )),
    ],
    attributes: tokenParsed.attributes || {},
  };
}
